import { useEffect } from 'react';
import { Col, Container, Row, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/useApiClient';
import ModalLeads from '../../components/Leads/modal/ModalLeads';
import {
	TabsEnum,
	LeadStatusEnum,
	RoleEnum,
	LeadsModalModeEnum,
} from '../../api/models/Enums';
import useLeadsStore from '../../store/LeadsStore';
import LeadsList from '../../components/Leads/LeadsComponents/LeadsList';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import i18next from 'i18next';
import './LeadsPage.scss';
import {
	ExportFormat,
	FileExportMenu,
} from 'src/components/FileExportMenu/FileExportMenu';
import { downloadXLSX, downloadCSV } from 'src/utils';
import toaster from 'src/utils/toaster';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import { useAuth } from 'src/hooks/useAuth';
import { ReactSelectOption } from 'src/types';
import useModalStore from 'src/store/ModalStore';
import { useSearchParams } from 'react-router-dom';
import { useLeads } from 'src/components/Leads/hooks';
import { useErrorToast } from 'src/hooks/useErrorToast';
import useColumnsLeadsList from 'src/components/Leads/tableColumns/useColumnsLeadsList';

const leadStatusByTab = (tab: TabsEnum) => {
	const status = {
		[TabsEnum.NEW_LEADS]: LeadStatusEnum.NEW,
		[TabsEnum.NURTURE_LEADS]: LeadStatusEnum.NURTURING,
		[TabsEnum.TO_BE_CONVERTED]: LeadStatusEnum.QUOTED,
		[TabsEnum.UNQUALIFIED_LEADS]: LeadStatusEnum.UNQUALIFIED,
	}[tab || 'UNKNOWN'];
	return status;
};

const BATCH_SIZE: number = 250;

const LeadsPage = () => {
	const { apiClient, handleApi } = useApiClient();
	const { t } = useTranslation();
	const {
		setDealers,
		setUserSalesOptions,
		setSalesUsers,
		setCheckedLeads,
		setActiveLead,
		setActiveTab,
		activeTab,
	} = useLeadsStore();
	const { showError } = useErrorToast();
	const { userRole, user } = useAuth();
	const [searchParams] = useSearchParams();
	const { openModal } = useModalStore();
	const leadId = searchParams.get('id') || null;
	const tableColumns = useColumnsLeadsList();

	useEffect(() => {
		handleApi(apiClient.dealerController.getRegisterDealers())
			.then((res) => {
				if (res) {
					setDealers(res);
				}
			})
			.catch((err) => {
				console.error(err);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const {
		leads,
		totalCount,
		isLoadingLeads,
		leadsError,
		updateLeadsFilter,
		currentLeadsFilter,
		resetFilter,
		fetchAllLeads,
		activeFiltersCount,
	} = useLeads({ status: leadStatusByTab(activeTab) });

	useEffect(() => {
		if (leadsError) {
			showError(leadsError, i18next.t('error_loading_data'));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadsError]);

	const handleLeadRedirect = async (id: string, isStale: () => boolean) => {
		try {
			const response = await handleApi(
				apiClient.leadsControllerService.getSingleLead(id)
			);

			if (isStale()) return;

			if (response?.Id !== id) {
				console.warn(`Lead with id ${id} not found.`);
				return;
			}

			const tabByStatus = {
				[LeadStatusEnum.NEW]: TabsEnum.NEW_LEADS,
				[LeadStatusEnum.NURTURING]: TabsEnum.NURTURE_LEADS,
				[LeadStatusEnum.QUOTED]: TabsEnum.TO_BE_CONVERTED,
				[LeadStatusEnum.UNQUALIFIED]: TabsEnum.UNQUALIFIED_LEADS,
			}[response.Status || 'unknown'];

			if (tabByStatus && tabByStatus !== activeTab) {
				setCheckedLeads([]);
				setActiveTab(tabByStatus);
				updateLeadsFilter(() => ({ status: leadStatusByTab(tabByStatus) }));
			}
			setActiveLead(response);
			openModal(LeadsModalModeEnum.DETAILS);
		} catch (error) {
			console.warn(error);
		}
	};

	useEffect(() => {
		if (!leadId) return;
		let stale = false;
		handleLeadRedirect(leadId, () => stale);
		return () => {
			stale = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [leadId]);

	//useEffect(() => {
	//  if (!response || !response.length || !leadId) return;
	//
	//  const foundLead: LeadEntity | undefined = response.find((lead: LeadEntity) => lead?.Id === leadId);
	//
	//  if (!foundLead) {
	//    console.warn(`Lead with id ${leadId} not found in result set.`);
	//    return;
	//  }
	//
	//  const tabByStatus = {
	//    [LeadStatusEnum.NEW]: TabsEnum.NEW_LEADS,
	//    [LeadStatusEnum.NURTURING]: TabsEnum.NURTURE_LEADS,
	//    [LeadStatusEnum.QUOTED]: TabsEnum.TO_BE_CONVERTED,
	//    [LeadStatusEnum.UNQUALIFIED]: TabsEnum.UNQUALIFIED_LEADS,
	//  }[foundLead.Status || 'unknown'];
	//
	//  tabByStatus && setActiveTab(tabByStatus)
	//  setActiveLead(foundLead);
	//
	//  setTimeout(() => {
	//    openModal(LeadsModalModeEnum.DETAILS);
	//    if (searchParams.has('id')) {
	//      searchParams.delete('id');
	//      setSearchParams(searchParams);
	//    }
	//  }, 100);
	//  // eslint-disable-next-line react-hooks/exhaustive-deps
	//}, [leadId, response]);

  useQuery({
    queryKey: ['fetch-sales-users'],
    queryFn: async () => {

      let salesUsers: any[] = [];
			const isAnySales = [
				RoleEnum.SALES,
				RoleEnum.SALES_DEALER,
				RoleEnum.SALES_INTERNAL,
			].includes(userRole)

      if (isAnySales) {
        salesUsers = await apiClient.userController.getRegisterSales();
      } else if (userRole === RoleEnum.AREA_MANAGER) {
        salesUsers = await apiClient.salesforceAccountController.getSalesUsers();

        const salesOptions: ReactSelectOption[] = salesUsers.map((mail) => ({
          label: mail,
          value: mail,
        }));

        setUserSalesOptions(salesOptions);
        return salesOptions;
      } else {
        salesUsers = await apiClient.userController.getRegisterUsers()
      }

			salesUsers = salesUsers.filter((user) =>
				[
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
				].includes(user.role)
			);
			setSalesUsers(salesUsers);

			if ([RoleEnum.BACKOFFICE, RoleEnum.AREA_MANAGER].includes(userRole)) {
				salesUsers = salesUsers.filter(
					(salesUser) => salesUser.reference_backoffice === user.username
				);
			}

			const salesOptions: ReactSelectOption[] = salesUsers.map((el) => ({
				label: `${el.family_name} ${el.given_name}`,
				value: el.username ?? '',
			}));
			setUserSalesOptions(salesOptions);
			return salesOptions;
		},
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: false,
		retry: false,
		refetchOnMount: false,
	});

	const getExportFileName = (currentTab: TabsEnum) => {
		switch (currentTab) {
			case TabsEnum.NEW_LEADS:
				return i18next.t('new_plural_female');
			case TabsEnum.NURTURE_LEADS:
				return i18next.t('nurturing_lead_plural');
			case TabsEnum.TO_BE_CONVERTED:
				return i18next.t('sent_quotation');
			case TabsEnum.UNQUALIFIED_LEADS:
				return i18next.t('unqualified_leads');
		}
	};

	const handleExportButton = async (format: ExportFormat) => {
		try {
			const apiCallWrapper = (params: any) =>
				handleApi(apiClient.leadsControllerService.pagedQuery(params));
			const dataToExport = await fetchAllLeads(
				apiCallWrapper,
				currentLeadsFilter,
				BATCH_SIZE
			);

			if (!dataToExport || dataToExport.length === 0) {
				toaster.error(i18next.t('no_data_available_for_download'));
				return;
			}

			const exportableColumns = tableColumns.filter(
				(col) => !['checkboxes', 'id', 'actions'].includes(col.id)
			);

			let fileName = [i18next.t('leads'), getExportFileName(activeTab)]
				.join('_')
				.replace(' ', '_');

			const downloadData = [];
			const headers = exportableColumns.map((col) => col.Header);
			headers?.push(i18next.t('motivation'))

			// @ts-ignore
			downloadData.push(headers);

			dataToExport.forEach((row) => {
				const rowValues = exportableColumns.map((col) => {
					let value = row[col.accessor];

					if (typeof value === 'string') {
						value = value.replace(/\n/g, ' ');
					}

					return value || '';
				});
				rowValues.push(row.Motivazione__c)
				// @ts-ignore
				downloadData.push(rowValues);
			});

			switch (format) {
				case 'xlsx':
					downloadXLSX(downloadData, fileName);
					break;
				case 'csv':
					downloadCSV(downloadData, fileName);
					break;
				default:
					console.warn(`[Horizon] Unsupported export format: ${format}`);
					break;
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_leads',
				evtAct: 'download',
				evtLab: `${fileName}.${format}`,
			});
		} catch (error) {
			showError(error, i18next.t('leads_export_error'));
		}
	};

	const handleTabSelect = (k: string | null) => {
		if (k === null) return;

		const selectedTab = k as TabsEnum;
		if (selectedTab === activeTab) return;
		setCheckedLeads([]);
		setActiveTab(selectedTab);
		// @ts-ignore
		updateLeadsFilter({ status: leadStatusByTab(selectedTab) });
	};

	return (
		<Container fluid id="leads">
			<Row>
				<Col md={6} className="display-6 mb-3">
					{t('leads')}
				</Col>
				<Col md={6} className="mt-4 mt-md-0 text-md-end">
					<FileExportMenu
						isDisabled={isLoadingLeads}
						allowedFormats={['csv', 'xlsx']}
						handleExport={handleExportButton}
					/>
				</Col>
			</Row>

			<Tabs activeKey={activeTab} className="mt-3" onSelect={handleTabSelect}>
				<Tab
					eventKey={TabsEnum.NEW_LEADS}
					title={i18next.t('new_plural_female')}
					className="tab"
					tabClassName="tab"
				/>

				<Tab
					eventKey={TabsEnum.NURTURE_LEADS}
					title={i18next.t('nurturing_lead_plural')}
					className="tab"
					tabClassName="tab"
				/>

				<Tab
					eventKey={TabsEnum.TO_BE_CONVERTED}
					title={i18next.t('sent_quotation')}
					className="tab"
					tabClassName="tab"
				/>

				<Tab
					eventKey={TabsEnum.UNQUALIFIED_LEADS}
					title={i18next.t('unqualified_leads')}
					className="tab"
					tabClassName="tab"
				/>
			</Tabs>
			<LeadsList
				key={`${activeTab}_leads`}
				filter={currentLeadsFilter}
				activeFiltersCount={activeFiltersCount}
				onResetFilter={() => {
					setCheckedLeads([]);
					resetFilter({ status: leadStatusByTab(activeTab) });
				}}
				loadFn={(filter) => {
					setCheckedLeads([]);
					// @ts-ignore
					updateLeadsFilter(filter);
				}}
				data={leads}
				error={leadsError}
				isVisible={true}
				totalCount={totalCount}
				isLoading={isLoadingLeads}
			/>
			<ModalLeads />
		</Container>
	);
};

export default LeadsPage;
